import React from 'react';
import { FiMenu } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { DocsMarkdown } from './DocsMarkdown';
import type { DocResponse } from '../../services/docsService';

interface Props {
  doc: DocResponse;
  group: string;
  onMobileToggle: () => void;
}

function formatLastUpdated(iso: string): string {
  const updated = new Date(iso);
  const now = new Date();
  const days = Math.floor((now.getTime() - updated.getTime()) / 86_400_000);
  if (days < 0) return updated.toLocaleDateString();
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return updated.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export const DocsContent: React.FC<Props> = ({ doc, group, onMobileToggle }) => {
  const { darkMode } = useTheme();
  const updated = formatLastUpdated(doc.last_updated);
  return (
    <div className="flex-1 px-4 lg:px-8 py-6 overflow-y-auto max-w-4xl">
      <header className={`mb-6 pb-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={onMobileToggle}
          className={`lg:hidden mb-3 flex items-center gap-1 px-2 py-1 text-sm rounded ${
            darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
          }`}
          aria-label="Open documentation menu"
        >
          <FiMenu className="w-4 h-4" /> Menu
        </button>
        <div className={`text-xs uppercase tracking-wider mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {group}
        </div>
        <h1 className="text-2xl font-semibold">{doc.title}</h1>
        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} title={doc.last_updated}>
          Updated {updated}
        </div>
      </header>
      <DocsMarkdown content={doc.content} size="medium" />
    </div>
  );
};
